<template>
    <CollapsibleCard
        class="substance-review-card mb-1"
        body-class="p-1"
        :default-collapsed="false"
    >
        <template #title>
            <div class="substance-review-card__title">
                <span class="substance-review-card__number">{{ number }}.</span>
                <div class="min-width-0">
                    <strong class="d-block">
                        {{ substance.name || translate('Component(ResearchReviewProcess):unnamed-substance', 'Unnamed substance') }}
                    </strong>
                    <div class="substance-review-card__summary mt-25">
                        <BBadge v-if="substance.casNumber" variant="light-secondary">
                            CAS: {{ substance.casNumber }}
                        </BBadge>
                        <BBadge v-if="substance.ecNumber" variant="light-secondary">
                            EC: {{ substance.ecNumber }}
                        </BBadge>
                        <small class="text-less-muted">{{ concentrationLabel }}</small>
                    </div>
                    <small v-if="summary" class="text-less-muted d-block mt-25">
                        {{ translate('Component(ResearchReviewProcess):matched-to', 'Matched to') }}:
                        {{ summary.name }}
                    </small>
                </div>
            </div>
        </template>

        <template #actions>
            <BSpinner v-if="hydrating" small variant="primary" class="mr-50" />
            <ReviewNeedsAttentionBadge :checks="evaluation.checks" />
            <BButton
                type="button"
                variant="flat-danger"
                size="sm"
                class="btn-icon mr-50"
                :class="{ 'ml-50': evaluation.status === ReviewCheckStatus.NEEDS_REVIEW }"
                :title="translate('Component(Button):remove', 'Remove')"
                @click.stop="$emit('remove')"
            >
                <FeatherIcon icon="Trash2Icon" size="16" />
            </BButton>
        </template>

        <BRow>
            <BCol cols="12" lg="7">
                <h6 class="mb-25">
                    {{ translate('Component(ResearchReviewProcess):extracted-substance', 'Extracted from document') }}
                </h6>
                <p class="text-less-muted small mb-1">
                    {{ translate(
                        'Component(ResearchReviewProcess):extracted-substance-description',
                        'Correct the substance information stated in the source document.',
                    ) }}
                </p>

                <BFormGroup
                    :label="translate('Component(Grid):column-name', 'Name')"
                    :state="nameViolationMessages.length ? false : null"
                    :invalid-feedback="nameViolationMessages.join(' ')"
                >
                    <BFormInput
                        ref="nameInput"
                        :value="substance.name"
                        :state="nameViolationMessages.length ? false : null"
                        @input="updateSubstance('name', $event)"
                    />
                </BFormGroup>
                <BRow>
                    <BCol cols="12" md="6">
                        <BFormGroup :label="translate('Component(SubstanceTable):column-cas', 'CAS number')">
                            <BFormInput
                                :value="substance.casNumber"
                                @input="updateSubstance('casNumber', $event || null)"
                            />
                        </BFormGroup>
                    </BCol>
                    <BCol cols="12" md="6">
                        <BFormGroup :label="translate('Component(SubstanceTable):column-ec', 'EC number')">
                            <BFormInput
                                :value="substance.ecNumber"
                                @input="updateSubstance('ecNumber', $event || null)"
                            />
                        </BFormGroup>
                    </BCol>
                </BRow>

                <label class="d-block mb-50">
                    {{ translate('Component(ResearchReviewProcess):concentration', 'Concentration') }}
                </label>
                <div class="substance-review-card__concentration">
                    <BFormSelect
                        :value="concentration.operator"
                        :options="operatorOptions"
                        @input="updateConcentration('operator', $event)"
                    />
                    <BFormInput
                        :value="concentration.value"
                        type="number"
                        step="any"
                        :placeholder="translate('Component(Label):value', 'Value')"
                        @input="updateConcentration('value', normalizeNumber($event))"
                    />
                    <BFormSelect
                        :value="concentration.unit"
                        :options="unitOptions"
                        @input="updateConcentration('unit', $event)"
                    />
                </div>
                <BFormGroup
                    v-if="showConcentrationBasis"
                    :label="translate('Component(ResearchReviewProcess):concentration-basis', 'Measurement basis')"
                    class="mt-75 mb-0"
                >
                    <VSelect
                        :value="selectedOption(basisOptions, concentration.basis)"
                        :options="basisOptions"
                        label="label"
                        :clearable="true"
                        :append-to-body="true"
                        @input="updateConcentration('basis', $event ? $event.id : null)"
                    />
                </BFormGroup>
            </BCol>

            <BCol cols="12" lg="5" class="mt-1 mt-lg-0">
                <div class="substance-review-card__match-panel">
                    <h6 class="mb-25">
                        {{ translate('Component(ResearchReviewProcess):chemity-substance', 'Chemity match') }}
                    </h6>
                    <p class="text-less-muted small mb-1">
                        {{ translate(
                            'Component(ResearchReviewProcess):chemity-substance-description',
                            'Confirm which Chemity substance this extracted row represents.',
                        ) }}
                    </p>

                    <SubstanceSelect
                        :value="summary"
                        :clearable="true"
                        :append-to-body="true"
                        :placeholder="translate('Component(Composition):search-placeholder', 'Search substances')"
                        :start-typing-text="translate('Component(Composition):search-label', 'Start typing to search')"
                        :no-options-text="translate('Component(Text):nothing-found', 'Nothing found')"
                        @input="$emit('select-link', $event)"
                    />

                    <div v-if="link" class="substance-review-card__link-details mt-1">
                        <div>
                            <small class="text-less-muted d-block">
                                {{ translate('Component(ResearchReviewProcess):link-type', 'Match type') }}
                            </small>
                            <span>{{ link.type || '-' }}</span>
                        </div>
                        <div>
                            <small class="text-less-muted d-block">
                                {{ translate('Component(ComplianceOverview):column-status', 'Status') }}
                            </small>
                            <span>{{ linkStatusLabel }}</span>
                        </div>
                        <div v-if="link.confidence !== null && typeof link.confidence !== 'undefined'">
                            <small class="text-less-muted d-block">
                                {{ translate('Component(ResearchReviewProcess):confidence', 'Confidence') }}
                            </small>
                            <span>{{ link.confidence }}</span>
                        </div>
                    </div>
                    <p v-if="link && link.reason" class="text-less-muted small mt-1 mb-0">
                        {{ link.reason }}
                    </p>
                </div>
            </BCol>
        </BRow>
    </CollapsibleCard>
</template>

<script>
import {
    BBadge,
    BButton,
    BCol,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
    BSpinner,
} from 'bootstrap-vue'
import CollapsibleCard from '@core/components/common/CollapsibleCard.vue'
import SubstanceSelect from '@core/components/common/SubstanceSelect.vue'
import ReviewNeedsAttentionBadge from '../components/ReviewNeedsAttentionBadge.vue'
import { ReviewCheckStatus } from './substance-review-checker-registry'
import VSelect from 'vue-select'
import { CONCENTRATION_BASES } from '../../payload/document-compliance-payload'

const DEFAULT_CONCENTRATION = Object.freeze({
    operator: '<=',
    value: null,
    unit: 'w/w',
    basis: null,
})

export default {
    name: 'SubstanceReviewCard',
    components: {
        BBadge,
        BButton,
        BCol,
        BFormGroup,
        BFormInput,
        BFormSelect,
        BRow,
        BSpinner,
        CollapsibleCard,
        ReviewNeedsAttentionBadge,
        SubstanceSelect,
        VSelect,
    },
    props: {
        number: { type: Number, required: true },
        substanceIndex: { type: Number, default: null },
        substance: { type: Object, required: true },
        link: { type: Object, default: null },
        summary: { type: Object, default: null },
        evaluation: { type: Object, required: true },
        hydrating: { type: Boolean, default: false },
        showConcentrationBasis: { type: Boolean, default: false },
        violations: { type: Array, default: () => [] },
    },
    data() {
        return {
            ReviewCheckStatus,
            operatorOptions: ['<=', '<', '=', '>=', '>'],
            unitOptions: [
                'w/w',
                'ppm',
                'µg/L',
                'mg dry substance/kg',
                'µg/dm²',
                'mg/kg',
                'mg/dm²',
            ],
            basisOptions: CONCENTRATION_BASES.map(value => ({
                id: value,
                label: this.translate(
                    `Component(ResearchReviewProcessEnum):concentration-basis-${value}`,
                    value === 'other' ? 'Other' : value,
                ),
            })),
        }
    },
    computed: {
        concentration() {
            const concentration = {
                ...DEFAULT_CONCENTRATION,
                ...(this.substance.concentration || {}),
            }

            return {
                ...concentration,
                basis: CONCENTRATION_BASES.includes(concentration.basis)
                    ? concentration.basis
                    : null,
            }
        },
        concentrationLabel() {
            if (this.concentration.value === null || typeof this.concentration.value === 'undefined') {
                return this.translate('Component(ResearchReviewProcess):concentration-not-stated', 'Concentration not stated')
            }

            return [
                this.concentration.operator,
                this.concentration.value,
                this.concentration.unit,
            ].filter(value => value !== null && typeof value !== 'undefined' && value !== '').join(' ')
        },
        linkStatusLabel() {
            const status = this.link && this.link.status ? this.link.status : 'uncertain'

            return this.translate(
                `Component(ResearchReviewProcessEnum):link-status-${status}`,
                status,
            )
        },
        nameViolationMessages() {
            return this.violationMessagesFor('name')
        },
    },
    methods: {
        violationMessagesFor(field) {
            const suffix = `[${field}]`

            return this.violations
                .filter(violation => violation
                    && String(violation.path || '').endsWith(suffix))
                .map(violation => violation.message)
                .filter(Boolean)
        },
        focusViolation(field) {
            const input = this.$refs[`${field}Input`]
            if (!input || typeof input.focus !== 'function') {
                return false
            }

            if (this.$el && typeof this.$el.scrollIntoView === 'function') {
                this.$el.scrollIntoView({ block: 'center' })
            }
            input.focus()

            return true
        },
        translate(key, fallback) {
            const translation = this.$t(key)

            return translation && translation !== key ? translation : fallback
        },
        updateSubstance(key, value) {
            this.$emit('input', {
                ...this.substance,
                [key]: value,
            })
        },
        updateConcentration(key, value) {
            this.updateSubstance('concentration', {
                ...this.concentration,
                [key]: value,
            })
        },
        normalizeNumber(value) {
            if (value === '' || value === null || typeof value === 'undefined') {
                return null
            }

            const number = Number(value)
            return Number.isFinite(number) ? number : null
        },
        selectedOption(options, value) {
            return options.find(option => option.id === value) || null
        },
    },
}
</script>

<style lang="scss" scoped>
.substance-review-card__title {
    display: flex;
    align-items: flex-start;
    gap: .75rem;
}

.substance-review-card__number {
    min-width: 1.25rem;
    font-weight: 600;
}

.substance-review-card__summary {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: .5rem;
}

.substance-review-card__concentration {
    display: grid;
    grid-template-columns: 5.5rem minmax(7rem, 1fr) minmax(9rem, 1fr);
    gap: .5rem;
}

.substance-review-card__match-panel {
    height: 100%;
    padding: 1rem;
    border: 1px solid #ebe9f1;
    border-radius: .357rem;
    background: #fafafa;
}

.substance-review-card__link-details {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: .75rem;
}

.min-width-0 {
    min-width: 0;
}

@media (max-width: 767.98px) {
    .substance-review-card__concentration {
        grid-template-columns: 1fr;
    }
}
</style>
